import { error, isHttpError, isRedirect } from '@sveltejs/kit';
import { HttpError } from '@tomra/client-side-http-client';

/** User-facing copy for a failed network connection (request never completed). */
export const NETWORK_ERROR_MESSAGE = "Network error — couldn't reach the server.";

/** User-facing copy for a request that was aborted after exceeding its timeout. */
export const TIMEOUT_ERROR_MESSAGE = 'The request timed out.';

/**
 * Extracts a user-friendly error message from an unknown error.
 *
 * For HttpError instances, includes the API error body when available
 * (e.g. "400 Bad Request: Validation error in the provided manifest data").
 * Network-class failures (no HTTP response) are mapped to friendly copy so
 * users don't see raw browser strings like "Failed to fetch". Otherwise falls
 * back to the error message or a default string.
 */
export function getErrorMessage(e: unknown, fallback = 'An unexpected error occurred'): string {
  if (e instanceof HttpError) {
    const detail = extractBodyMessage(e.body);
    const base = `${e.status} ${e.statusText}`;
    return detail ? `${base}: ${detail}` : base;
  }
  if (isTimeoutError(e)) {
    return TIMEOUT_ERROR_MESSAGE;
  }
  if (isNetworkError(e)) {
    return NETWORK_ERROR_MESSAGE;
  }
  if (e instanceof Error) {
    return e.message;
  }
  if (typeof e === 'object' && e !== null) {
    const obj = e as Record<string, unknown>;
    if ('status' in obj && typeof obj.status === 'number' && 'body' in obj) {
      const detail = extractBodyMessage(obj.body as string | Record<string, unknown>);
      const statusText = typeof obj.statusText === 'string' ? obj.statusText : '';
      const base = statusText ? `${obj.status} ${statusText}` : `${obj.status}`;
      return detail ? `${base}: ${detail}` : base;
    }
    const message = extractBodyMessage(obj);
    if (message) return message;
  }
  return String(e) || fallback;
}

/** Formats in-page errors without HTTP status prefixes or diagnostic identifiers. */
export function getUserFacingErrorMessage(
  e: unknown,
  fallback = 'An unexpected error occurred. Please try again.'
): string {
  if (isTimeoutError(e)) return TIMEOUT_ERROR_MESSAGE;
  if (isNetworkError(e)) return NETWORK_ERROR_MESSAGE;
  if (e instanceof HttpError) {
    return extractBodyMessage(e.body, true) ?? fallback;
  }
  if (typeof e === 'object' && e !== null) {
    const obj = e as Record<string, unknown>;
    if (typeof obj.status === 'number' && 'body' in obj) {
      return extractBodyMessage(obj.body, true) ?? fallback;
    }
    return extractBodyMessage(obj, true) ?? fallback;
  }
  return typeof e === 'string' && e.trim() ? e.trim() : fallback;
}

/**
 * True when the error is a request abort caused by the client-side timeout.
 * The HTTP client aborts via AbortController, which rejects with an
 * AbortError-named DOMException.
 */
export function isTimeoutError(e: unknown): boolean {
  return e instanceof Error && e.name === 'AbortError';
}

/**
 * True when the error is a network-class failure (DNS, offline, connection
 * refused, CORS) where the request never produced an HTTP response. Browsers
 * throw a TypeError with a browser-specific message for these.
 */
export function isNetworkError(e: unknown): boolean {
  return e instanceof TypeError && /failed to fetch|networkerror|load failed/i.test(e.message);
}

/**
 * True for API failures the user can recover from in-page by changing their
 * input or filter selection (e.g. invalid softwareModuleType or date filter -> 400/404).
 * Auth failures (401/403) are excluded so they bubble to the global error boundary,
 * which handles re-authentication/redirects. Server (5xx) and network failures also
 * fall through to the full-page boundary.
 */
export function isRecoverableClientError(e: unknown): boolean {
  const status =
    e instanceof HttpError
      ? e.status
      : typeof e === 'object' &&
          e !== null &&
          'status' in e &&
          typeof (e as { status: unknown }).status === 'number'
        ? (e as { status: number }).status
        : undefined;

  return status !== undefined && status >= 400 && status < 500 && status !== 401 && status !== 403;
}

function extractBodyMessage(body: unknown, userFacing = false): string | undefined {
  let parsed: Record<string, unknown> | undefined;

  if (typeof body === 'string') {
    const trimmed = body.trim();
    if (!trimmed) return undefined;
    if (userFacing && /^[{[]/.test(trimmed) && !/[}\]]$/.test(trimmed)) return undefined;

    if (
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
      try {
        const json = JSON.parse(trimmed);
        if (typeof json === 'object' && json !== null && !Array.isArray(json)) {
          parsed = json as Record<string, unknown>;
        }
      } catch {
        // Not valid JSON
      }
      if (userFacing && !parsed) return undefined;
    }

    if (!parsed) {
      return trimmed;
    }
  } else if (typeof body === 'object' && body !== null) {
    parsed = body as Record<string, unknown>;
  }

  if (parsed) {
    const rawTitle =
      typeof parsed.title === 'string' && parsed.title.trim() ? parsed.title.trim() : undefined;
    const title =
      userFacing &&
      rawTitle &&
      /^(bad request|internal server error|not found|resource not found|unauthorized|forbidden|access forbidden|not acceptable|precondition failed|unsupported media type|service unavailable|conflict)$/i.test(
        rawTitle
      )
        ? undefined
        : rawTitle;
    const detail =
      typeof parsed.detail === 'string' && parsed.detail.trim() ? parsed.detail.trim() : undefined;

    if (userFacing && detail) return detail;

    const errorId =
      typeof parsed.errorId === 'string' && parsed.errorId.trim()
        ? parsed.errorId.trim()
        : typeof parsed.requestId === 'string' && parsed.requestId.trim()
          ? parsed.requestId.trim()
          : undefined;

    let mainMessage: string | undefined;

    if (title && detail) {
      if (title.toLowerCase() === detail.toLowerCase()) {
        mainMessage = detail;
      } else if (
        /^(bad request|internal server error|not found|resource not found|unauthorized|forbidden|not acceptable|precondition failed|unsupported media type)$/i.test(
          title
        )
      ) {
        mainMessage = detail;
      } else {
        mainMessage = `${title}: ${detail}`;
      }
    } else if (detail) {
      mainMessage = detail;
    } else if (title) {
      mainMessage = title;
    } else {
      for (const key of [
        'error',
        'message',
        'description',
        'errorMessage',
        'userMessage',
        'reason'
      ]) {
        const value = parsed[key];
        if (typeof value === 'string' && value.trim()) {
          mainMessage = value.trim();
          break;
        }
      }

      if (!mainMessage && Array.isArray(parsed.errors) && parsed.errors.length > 0) {
        const items = parsed.errors
          .map((item) => {
            if (typeof item === 'string') return item.trim();
            if (typeof item === 'object' && item !== null) {
              const obj = item as Record<string, unknown>;
              return (
                (typeof obj.message === 'string' && obj.message.trim()) ||
                (typeof obj.detail === 'string' && obj.detail.trim()) ||
                (typeof obj.error === 'string' && obj.error.trim()) ||
                ''
              );
            }
            return '';
          })
          .filter(Boolean);

        if (items.length > 0) {
          mainMessage = items.join('; ');
        }
      }
    }

    if (mainMessage) {
      if (!userFacing && errorId && !mainMessage.includes(errorId)) {
        return `${mainMessage} (Error ID: ${errorId})`;
      }
      return mainMessage;
    }
  }

  return undefined;
}

/**
 * Handles errors in SvelteKit load functions by converting API HttpErrors (and other errors)
 * into SvelteKit error responses with proper HTTP status code and API error detail message.
 */
export function handleLoadError(e: unknown): never {
  if (isHttpError(e) || isRedirect(e)) {
    throw e;
  }

  const status =
    e instanceof HttpError
      ? e.status
      : typeof e === 'object' &&
          e !== null &&
          'status' in e &&
          typeof (e as { status: unknown }).status === 'number'
        ? (e as { status: number }).status
        : 500;

  throw error(status, getErrorMessage(e));
}
